import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { Users, X, RefreshCw, Trash2, Link2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface Course {
  id: number
  name: string
  fach: string
  jahrgang: string
}

interface Group {
  id: number
  name: string
  gruppen_typ: string
}

interface Participant {
  id: string
  first_name: string
  last_name: string
  klasse: string
}

interface ClassLink {
  class_id: number
  class_name: string
  source: 'manual' | 'timetable'
}

interface ClassOption {
  id: number
  class_name: string
}

interface ParticipantsModalProps {
  type: 'course' | 'group' | 'class'
  item: Course | Group | Class
  onClose: () => void
  onRemoveStudent: (studentId: string) => void
  isLoading: boolean
  /** Stichtag für Mitgliederliste (YYYY-MM-DD); null = heute */
  standAsOf?: string | null
}

interface Class {
  id: number
  class_name: string
  name?: string
}

export default function ParticipantsModal({ type, item, onClose, onRemoveStudent, isLoading, standAsOf }: ParticipantsModalProps) {
  const [participants, setParticipants] = useState<Participant[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [classLinks, setClassLinks] = useState<ClassLink[]>([])
  const [allClasses, setAllClasses] = useState<ClassOption[]>([])
  const [selectedClassIds, setSelectedClassIds] = useState<Set<number>>(new Set())
  const [savingLinks, setSavingLinks] = useState(false)
  const [poolCount, setPoolCount] = useState<number | null>(null)

  const transformMembers = (members: any[]): Participant[] =>
    members.map((member: any) => ({
      id: member.sophomorixUnid || member.Student_ID || member.username || String(member.user_id),
      first_name: member.Vorname || member.first_name || '',
      last_name: member.Name || member.last_name || '',
      klasse: member.Klasse || ''
    }))

  const reloadCourseData = async () => {
    const params = standAsOf ? { date: standAsOf } : {}
    const [linksRes, membersRes, poolRes] = await Promise.all([
      api.get(`/memberships/units/course/${item.id}/class-links`),
      api.get(`/memberships/units/course/${item.id}/members`, { params }),
      api.get(`/memberships/units/course/${item.id}/class-pool`, { params }).catch(() => ({ data: { data: [] } }))
    ])
    const links = linksRes.data.data || []
    setClassLinks(links)
    setSelectedClassIds(new Set(links.map((l: ClassLink) => l.class_id)))
    setPoolCount(Array.isArray(poolRes.data.data) ? poolRes.data.data.length : null)
    setParticipants(transformMembers(membersRes.data.data || []))
  }

  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        setLoading(true)
        if (type === 'course') {
          const classesRes = await api.get('/units/class', { params: { is_active: 'true' } })
          setAllClasses(classesRes.data.data || [])
          await reloadCourseData()
          return
        }

        const endpoint = `/memberships/units/${type}/${item.id}/members`
        const params = standAsOf ? { date: standAsOf } : {}
        const response = await api.get(endpoint, { params })
        setParticipants(transformMembers(response.data.data || []))
        setClassLinks([])
        setPoolCount(null)
      } catch (error) {
        console.error('Error fetching participants:', error)
        toast.error('Fehler beim Laden der Teilnehmer')
      } finally {
        setLoading(false)
      }
    }

    void fetchParticipants()
  }, [type, item.id, standAsOf])

  const filteredParticipants = participants.filter(participant => {
    if (!participant) return false

    const fullName = `${participant.first_name || ''} ${participant.last_name || ''}`.toLowerCase()
    const klasse = (participant.klasse || '').toLowerCase()
    const search = searchTerm.toLowerCase()
    return fullName.includes(search) || klasse.includes(search)
  })

  const toggleParticipant = (participantId: string) => {
    const newSelection = new Set(selectedParticipants)
    if (newSelection.has(participantId)) {
      newSelection.delete(participantId)
    } else {
      newSelection.add(participantId)
    }
    setSelectedParticipants(newSelection)
  }

  const handleSelectAll = () => {
    if (selectedParticipants.size === filteredParticipants.length) {
      setSelectedParticipants(new Set())
    } else {
      setSelectedParticipants(new Set(filteredParticipants.map(p => p.id)))
    }
  }

  const handleBulkRemove = () => {
    if (selectedParticipants.size === 0) {
      toast.error('Bitte wählen Sie mindestens einen Teilnehmer aus')
      return
    }

    const participantNames = participants
      .filter(p => selectedParticipants.has(p.id))
      .map(p => `${p.first_name} ${p.last_name}`)
      .join(', ')

    if (window.confirm(`Möchten Sie ${selectedParticipants.size} Teilnehmer wirklich entfernen?\n\n${participantNames}`)) {
      selectedParticipants.forEach(participantId => {
        onRemoveStudent(participantId)
      })
      setSelectedParticipants(new Set())
    }
  }

  const toggleClassSelection = (classId: number) => {
    const next = new Set(selectedClassIds)
    if (next.has(classId)) next.delete(classId)
    else next.add(classId)
    setSelectedClassIds(next)
  }

  const saveClassLinks = async () => {
    try {
      setSavingLinks(true)
      await api.put(`/memberships/units/course/${item.id}/class-links`, {
        class_ids: Array.from(selectedClassIds)
      })
      toast.success('Klassen-Verknüpfungen gespeichert')
      await reloadCourseData()
    } catch (error) {
      console.error(error)
      toast.error('Klassen konnten nicht gespeichert werden')
    } finally {
      setSavingLinks(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
      <div className="card flex max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
            Teilnehmer von {type === 'course' ? 'Kurs' : type === 'group' ? 'Gruppe' : 'Klasse'} verwalten
          </h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title="Schließen"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {item?.name || (item as any)?.class_name} - {type === 'course' ? 'Kurs' : type === 'group' ? 'Gruppe' : 'Klasse'}
        </p>

        {type === 'course' && (
          <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/70 p-3 dark:border-indigo-800 dark:bg-indigo-950/30">
            <div className="flex items-center gap-2 text-sm font-medium text-indigo-900 dark:text-indigo-100 mb-2">
              <Link2 className="w-4 h-4" />
              Verknüpfte Klassen
            </div>
            <p className="text-xs text-indigo-800 dark:text-indigo-200 mb-3">
              Die verknüpften Klassen definieren den Auswahl-Pool. Kurs-Teilnehmer werden daraus
              einzeln ausgewählt (über „Schüler verwalten“). Im Klassenbuch gilt nur die Auswahl.
            </p>
            <div className="max-h-32 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
              {allClasses.map((cls) => (
                <label key={cls.id} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedClassIds.has(cls.id)}
                    onChange={() => toggleClassSelection(cls.id)}
                  />
                  <span>{cls.class_name}</span>
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={() => void saveClassLinks()}
              disabled={savingLinks}
              className="text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingLinks ? 'Speichern…' : 'Klassen speichern'}
            </button>
            {classLinks.length > 0 && (
              <div className="mt-2 text-xs text-indigo-700 dark:text-indigo-300">
                Pool: {poolCount ?? '…'} Schüler aus {classLinks.map((l) => l.class_name).join(', ')}
                {' · '}
                {participants.length} ausgewählt
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field"
              placeholder="Nach Name oder Klasse suchen..."
            />
          </div>
        </div>

        <>
          <div className="mb-4">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              {selectedParticipants.size === filteredParticipants.length && filteredParticipants.length > 0
                ? 'Alle abwählen'
                : 'Alle auswählen'}
            </button>
            <span className="text-sm text-gray-500 ml-2">
              ({selectedParticipants.size} von {filteredParticipants.length} ausgewählt)
            </span>
          </div>

          {selectedParticipants.size > 0 && (
            <div className="mb-4">
              <button
                onClick={handleBulkRemove}
                disabled={isLoading}
                className="px-4 py-2 text-sm bg-red-100 text-red-700 border border-red-300 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="w-4 h-4 inline mr-2" />
                Ausgewählte entfernen ({selectedParticipants.size})
              </button>
            </div>
          )}
        </>

        {type === 'course' && classLinks.length > 0 && participants.length === 0 && !loading && (
          <div className="mb-4 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
            Noch keine Kurs-Teilnehmer ausgewählt. Nutzen Sie „Schüler verwalten“, um Schüler aus dem
            Klassen-Pool hinzuzufügen.
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 text-gray-400 animate-spin" />
              <p className="text-gray-500">Teilnehmer werden geladen...</p>
            </div>
          ) : filteredParticipants.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">
                {searchTerm
                  ? 'Keine Teilnehmer gefunden'
                  : type === 'course'
                  ? classLinks.length > 0
                    ? 'Keine Kurs-Teilnehmer ausgewählt — bitte Schüler aus dem Klassen-Pool hinzufügen'
                    : 'Keine Teilnehmer — bitte Klassen verknüpfen und Schüler auswählen'
                  : `Keine Teilnehmer in dieser ${type === 'group' ? 'Gruppe' : 'Klasse'}`}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredParticipants.map((participant) => (
                <div
                  key={participant.id}
                  className={`flex items-center p-3 border rounded-lg transition-colors cursor-pointer ${
                    selectedParticipants.has(participant.id)
                      ? 'bg-primary-50 border-primary-200'
                      : 'border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => toggleParticipant(participant.id)}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                      selectedParticipants.has(participant.id)
                        ? 'bg-primary-600 border-primary-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedParticipants.has(participant.id) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {participant.first_name || 'Unbekannt'} {participant.last_name || 'Name'}
                      </h4>
                      <p className="text-xs text-gray-600">
                        Klasse {participant.klasse || 'Unbekannt'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  )
}
